import {
  ValidatorFn, AbstractControl, ValidationErrors, AsyncValidatorFn
} from '@angular/forms';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { CoreDataService } from 'shared/services/core-data.service';

export const VALIDATORS: any = {
  PATTERN: {
    FOUR_DIGITS_ZIP: '^([0]{1}[1-9]{1}|[1-9]{1}[0-9]{1})[0-9]{2}$',
    FIVE_DIGITS_ZIP: '^([0]{1}[1-9]{1}|[1-9]{1}[0-9]{1})[0-9]{3}$',
    STREET_NR: '^[1-9][0-9a-zA-Z\s\/\-]{0,19}$',
    MAIL: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$',
    PHONE: '^((\\+|00)?(\\d){2}\\s?([1-9]){1}([\\d\\-\\s]){4,14})$',
    FAX: '^(((\\+|00)?(\\d){2}\\s?([1-9]){1}([\\d\\-\\s]){4,14}))$',
    POSITION_NR: '^[1-9][0-9]*$',
    CURRENCY: '^[+-]?[0-9]+\\.?[0-9]{0,2}$',
    FIVE_DIGIT_DECIMAL: '^[0-9]+(\\.[0-9]{1,5})?$',
    NO_BLANK_STRING: '^.*\\S.*$',
    WEBSITE: '^(https?:\\/\\/)?(([\\w\\-]+\\.)+[a-zA-Z]{2,})(:\\d{1,5})?([\\/?#]\\S*)?$'
  },
  MAX_LENGTH: {
    INPUT: 70,
    INPUT_SHORT: 40,
    INPUT_LONG: 120,
    TEXTAREA: 600,
    TEXTEDITOR: 2000,
    FREE_TEXT: 10,
    LV_POSITION_TEXT: 57
  },
  MIN: {
    VAT: 0,
    TZ: 0,
    SURCHARGE: 0,
    DISCOUNT: 0,
    MIN_MARGIN: 0,
    ARTICLE_LIST_PRICE: 0,
    ARTICLE_PURCHASE_PRICE: 0,
    MEASUREMENT_CALCULATIONS: 0
  },
  MAX: {
    VAT: 99,
    TZ: 100,
    SURCHARGE: 100,
    DISCOUNT: 100,
    MIN_MARGIN: 99.99,
    ARTICLE_LIST_PRICE: 9999.99,
    ARTICLE_PURCHASE_PRICE: 9999.99,
    MEASUREMENT_CALCULATIONS: 5000
  }
};

export const zipCodeValidationsPerCountry: Record<string, { valPattern: string }> = {
  DE: {
    valPattern: VALIDATORS.PATTERN.FIVE_DIGITS_ZIP
  },
  PL: {
    valPattern: VALIDATORS.PATTERN.FIVE_DIGITS_ZIP
  },
  CH: {
    valPattern: VALIDATORS.PATTERN.FOUR_DIGITS_ZIP
  },
  AT: {
    valPattern: VALIDATORS.PATTERN.FOUR_DIGITS_ZIP
  }
};

export const zipCodeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.parent) return null;

  const country = control.parent.get('country');
  const zipCode = control.parent.get('zipCode');
  let res;

  if (!country || !zipCode) return null;

  if (country.value in zipCodeValidationsPerCountry) {
    res = zipCode.value.match(zipCodeValidationsPerCountry[country.value].valPattern);

    if (res && res.length > 0) {
      zipCode.setErrors(null);

      return null;
    }
    zipCode.markAsTouched();
    zipCode.setErrors({ invalidZipCode: true });

    if (zipCode === control) {
      return { invalidZipCode: true };
    }
  }

  return null;
};

export function validateUniqueObjectNumber(
  coreDataService: CoreDataService,
  objectId?: string
): AsyncValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) {
      return of(null);
    }

    return coreDataService.getObjectByNumber(control.value)
      .pipe(
        map((objects) => {
          if (
            (!objectId && objects.length > 0)
            || (objectId && objects.filter((object) => object.id !== objectId).length > 0)
          ) {
            return { uniqueRequired: { message: 'FORMS.ERROR.OBJECT.UNIQUE_REQUIRED' } };
          } else {
            return null;
          }
        }),
        take(1),
        catchError(() => of(null))
      );
  };
}

export const validateEmailsList: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;

  const emailsPhonesArray: string[] = (typeof control.value === 'string' ? [control.value] : control.value);

  const isValid = emailsPhonesArray.every((entry: string) => {
    const emailRegex = new RegExp(VALIDATORS.PATTERN.MAIL); //);

    return emailRegex.test(entry.trim());
  });

  return isValid ? null : { invalidEmailList: true };
}
